import { Icon, IconSize, IconVariants } from "@next-platform/client-common-spicy-ui";
import { LoadingForType } from "@next-platform/client-common-core";
import {
  Card,
  CardBody,
  CardFooter,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownSection,
  DropdownTrigger,
  Image,
  Spinner,
} from "@nextui-org/react";
import { useCallback, useState } from "react";

import { LocalizedLink } from "@/components/LocalizedLink/LocalizedLink";
import Trans from "@/components/Trans/Trans";
import { EDIT_PERSONA_PAGE_URL } from "@/constants";
import { AvatarPreset } from "@/types";
import { getAvatarUrl } from "@/utils";

import TextStyles from "../common/TextStyles";
import { Col, Row, SpicyButton, Text } from "../customComponents";
import { PersonaCardProps } from "./ChoosePersona/types";

const PersonaIcon = "/Assets/persona.svg";
const DROPDOWN_ROW_GAP = 0.5;

export default function MyPersonasCard(props: PersonaCardProps) {
  const {
    data,
    defaultPersonaId,
    loading,
    onUserStartDeletingPersona,
    onUserSelectsPersonaToDelete,
    onUserMakePersonaToDefault,
    setIsShowWarningModal,
  } = props;
  const { id, avatar_url, name, highlights } = data;
  const isDefault = defaultPersonaId === id;
  const [isAction, setIsAction] = useState(false);
  const isSetDefaultPersonaLoading = isAction && loading?.loadingType === LoadingForType.setDefaultPersona && loading.isLoading;

  const handleDeletePersona = useCallback(() => {
    if (isDefault) {
      setIsShowWarningModal?.(true);
    } else {
      onUserStartDeletingPersona?.();
      onUserSelectsPersonaToDelete?.(id);
    }
  }, [isDefault]);

  const handleDefaultPersona = async () => {
    setIsAction(true);
    await onUserMakePersonaToDefault?.(id);
    setIsAction(false);
  };

  return (
    <Card className="w-full max-w-[180px]">
      {isSetDefaultPersonaLoading ? <Spinner className="absolute z-20 top-0 left-0 pb-8 bg-[#00000050] h-full w-full" /> : null}
      {isDefault ? <Image src={PersonaIcon} className="absolute z-[99] top-1.5 left-2 size-6 rounded-none" /> : null}
      <CardBody className="p-0 overflow-hidden flex-none">
        <Dropdown placement="top" crossOffset={-70} classNames={{ trigger: "absolute fill-black top-2 right-1 z-[99]" }}>
          <DropdownTrigger className="py-1 -mt-1 !bg-[rgb(0,0,0,0.4)]">
            <SpicyButton isIconOnly variant="primary_smallest">
              <Icon icon={IconVariants.THREE_DOTS} size={IconSize.SMALL} className="text-white opacity-90" />
            </SpicyButton>
          </DropdownTrigger>
          <DropdownMenu classNames={TextStyles.dropdown.menu} className="w-full">
            <DropdownSection classNames={TextStyles.dropdown.section} hidden={isDefault}>
              <DropdownItem classNames={TextStyles.dropdown.item_primary} onClick={handleDefaultPersona}>
                <Row justify="between" gap={DROPDOWN_ROW_GAP} className="w-full" style={undefined}>
                  <Text variant={"md"}>
                    <Trans i18nKey="personas:personaCard.dropdown.makeDefault" />
                  </Text>{" "}
                  <Image src={PersonaIcon} className="size-5" />
                </Row>
              </DropdownItem>
            </DropdownSection>
            <DropdownSection classNames={TextStyles.dropdown.section}>
              <DropdownItem classNames={TextStyles.dropdown.item_danger} color="danger" onClick={handleDeletePersona}>
                <Row justify="between" gap={DROPDOWN_ROW_GAP} className="w-full" style={undefined}>
                  <Text variant={"md"} type={"error"} mNone={undefined}>
                    <Trans i18nKey="common:delete" />
                  </Text>{" "}
                  <Icon icon={IconVariants.TRASH} size={IconSize.MEDIUM} className="text-red-600" />
                </Row>
              </DropdownItem>
            </DropdownSection>
          </DropdownMenu>
        </Dropdown>
        <LocalizedLink className="h-[180px]" to={`${EDIT_PERSONA_PAGE_URL}/${id}`} aria-label="edit-persona">
          {avatar_url ? (
            <Image
              src={getAvatarUrl(avatar_url, { preset: AvatarPreset.AVATAR_256X256 }) as string}
              alt={name}
              loading="lazy"
              height={"100%"}
              width={"100%"}
              className="object-cover opacity-100"
              radius="none"
            />
          ) : (
            <div className="bg-zinc-300 text-gray-600 max-h-[180px] w-full h-full rounded-2xl rounded-br-none rounded-bl-none flex items-center justify-center">
              <Icon icon={IconVariants.USER} size={IconSize.XLARGE} />
            </div>
          )}
        </LocalizedLink>
      </CardBody>
      <CardFooter className="p-2 px-3">
        <Col gap={0.2}>
          <Text b variant={"xs"}>
            {name}
          </Text>
          <Text i variant={"xs"} type="[overflow-wrap:anywhere] line-clamp-3">
            {highlights}
          </Text>
        </Col>
      </CardFooter>
    </Card>
  );
}
